<script setup lang="ts">
import { Component, State} from "../types.ts";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import useStore from "../store.ts";

const {item} = defineProps<{
  item: Component
}>();

let selectionBackground = "inherit";
let selectionForeground = "inherit";
let disabledBackground = "inherit";
let disabledForeground = "inherit";
let pressedBackground = "inherit";
let pressedForeground = "inherit";


if (item.submit_button_props) {
  if (item.submit_button_props.selection_colors) {
    selectionBackground = item.submit_button_props.selection_colors.background ?? "inherit";
    selectionForeground = item.submit_button_props.selection_colors.foreground ?? "inherit";
  }

  if (item.submit_button_props.disabled_colors) {
    disabledBackground = item.submit_button_props.disabled_colors.background ?? "inherit";
    disabledForeground = item.submit_button_props.disabled_colors.foreground ?? "inherit";
  }

  if (item.submit_button_props.pressed_colors) {
    pressedBackground = item.submit_button_props.pressed_colors.background ?? "inherit";
    pressedForeground = item.submit_button_props.pressed_colors.foreground ?? "inherit";
  }
}

const stateToPseudo: Partial<Record<State, string>> = {
  [State.ACTIVE]: ":active",
  [State.CHECKED]: ":checked",
  [State.DISABLED]: ":disabled",
  [State.EMPTY]: ":empty",
  [State.ENABLED]: ":enabled",
  [State.FOCUS]: ":focus",
  [State.HOVER]: ":hover",
  [State.INVALID]: ":invalid",
  [State.LINK]: ":link",
  [State.OPTIONAL]: ":optional",
  [State.REQUIRED]: ":required",
  [State.VALID]: ":valid",
  [State.VISITED]: ":visited",
};

const buttonClass = computed(
  () => `data-object-submit-button-${(item.props?.id || "button").replace(/[^a-zA-Z0-9_-]/g, "-")}`
);

const stateStylesCSS = computed(() => {
  const styles = item.submit_button_props?.state_style || [];
  return styles
    .map(({state, css_style}) => {
      const pseudo = stateToPseudo[state];
      if (!pseudo || !css_style) return "";
      return `.${buttonClass.value}${pseudo}{${css_style}}`;
    })
    .filter(Boolean)
    .join("\n")
    .trim();
});

const styleEl = ref<HTMLStyleElement | null>(null);

const ensureStyleEl = () => {
  if (styleEl.value) return styleEl.value;
  const el = document.createElement("style");
  el.dataset.buttonStyle = item.props?.id || "button";
  document.head.appendChild(el);
  styleEl.value = el;
  return el;
};

onMounted(() => {
  if (!stateStylesCSS.value) return;
  const el = ensureStyleEl();
  el.textContent = stateStylesCSS.value;
});

watch(stateStylesCSS, (css) => {
  if (!css) {
    if (styleEl.value?.parentNode) {
      styleEl.value.parentNode.removeChild(styleEl.value);
    }
    styleEl.value = null;
    return;
  }
  const el = ensureStyleEl();
  el.textContent = css;
});

onBeforeUnmount(() => {
  if (styleEl.value?.parentNode) {
    styleEl.value.parentNode.removeChild(styleEl.value);
  }
  styleEl.value = null;
});

onMounted(() => {
  const store = useStore();
  store.socketManager.requestButtonStatus(item);
});



const handleClick = async () => {
  const store = useStore();
  for (const [key, value] of store.submitions.entries()) {
    if (value == item.props.id) {
      const target = store.components[key];
      if (!target) continue;
      store.socketManager.sendTextUpdate(target, null, true);
    }
  }
};

</script>
<template>
  <button
      :class="buttonClass"
      :style="item.props.style"
      @click="handleClick"
  >
    <p :id="`button-${item.props.id}`"
       :style="{textAlign: item.props.style['text-align']}"
       v-html="item.published.src">
    </p>
    <slot>
    </slot>
  </button>
</template>

<style scoped>
div {
  cursor: pointer;
}

button:focus {
  background: v-bind(selectionBackground) !important;
  color: v-bind(selectionForeground) !important;
}

button:disabled {
  background: v-bind(disabledBackground) !important;
  color: v-bind(disabledForeground) !important;
}

button:active {
  background: v-bind(pressedBackground) !important;
  color: v-bind(pressedForeground) !important;
}

button p {
  margin: 0;
  margin-block: 0;
  padding: 0;
  left: 0;
  right: 0;
  top: 0;
  position: relative;
}

</style>